import { Tooltip } from '@mantine/core';
import { Icon, variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { ArticleMetricsInfo } from 'app/modules/wallforming/components/content-container/scene-status-bar/ArticleMetricsInfo';
import { ArticlesCountInfo } from 'app/modules/wallforming/components/content-container/scene-status-bar/ArticlesCountInfo';
import { ArticleSelectionInfo } from 'app/modules/wallforming/components/content-container/scene-status-bar/ArticleSelectionInfo';
import { DimensionOptions } from 'app/modules/wallforming/components/content-container/scene-status-bar/dimension-controls/DimensionOptions';
import { HeatmapControls } from 'app/modules/wallforming/components/content-container/scene-status-bar/heatmap-controls/HeatmapControls';
import { PlacementUpdateInfo } from 'app/modules/wallforming/components/content-container/scene-status-bar/PlacementUpdateInfo';
import { ProjectSaveStatusInfo } from 'app/modules/wallforming/components/content-container/scene-status-bar/ProjectSaveStatusInfo';
import { LockIcon } from 'app/modules/wallforming/components/scenes/toolbar/content/LockIcon';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { useIsStandaloneMode } from 'shared/hooks/useIsStandaloneMode';

import 'app/modules/wallforming/custom-styles/customButtonStyle.css';

export const SceneStatusBar = () => {
    const { t } = useTranslation();
    const isStandaloneMode = useIsStandaloneMode();
    const { showSceneStatusBarControls, setShowSceneStatusBarControls } = usePlacementStore(
        useShallow((state) => ({
            showSceneStatusBarControls: state.showSceneStatusBarControls,
            setShowSceneStatusBarControls: state.setShowSceneStatusBarControls
        }))
    );

    return (
        <div className="absolute bottom-0 left-0 right-0 z-40 h-11 sm:h-11 flex items-center justify-between border-t border-border-ultra-light bg-background-default backdrop-blur-sm select-none font-mono">
            <div className="scene-status-scroll flex flex-1 min-w-0 items-stretch justify-start">
                <div className="flex shrink-0 items-center justify-start border-r border-border-ultra-light pl-2 sm:pl-2 pr-0.5 sm:pr-0.5 cursor-pointer">
                    {showSceneStatusBarControls && (
                        <>
                            <LockIcon />
                            <DimensionOptions />
                            <HeatmapControls />
                        </>
                    )}
                    <Tooltip
                        label={
                            showSceneStatusBarControls
                                ? t('hide', { defaultValue: 'Hide status controls' })
                                : t('show', { defaultValue: 'Show status controls' })
                        }
                        position="bottom"
                        variant={variants.tooltip.bottom}
                    >
                        <button
                            aria-label={showSceneStatusBarControls ? 'Hide status controls' : 'Show status controls'}
                            className="flex h-8.75 ml-1 cursor-pointer items-center justify-center border border-border-ultra-light bg-background-gray-light text-text-default transition-colors hover:bg-background-gray! focus-visible:outline-none"
                            type="button"
                            onPointerDown={() => setShowSceneStatusBarControls(!showSceneStatusBarControls)}
                        >
                            <Icon
                                className="text-[0.9rem] leading-none"
                                icon={
                                    showSceneStatusBarControls
                                        ? 'keyboard_double_arrow_left'
                                        : 'keyboard_double_arrow_right'
                                }
                            />
                        </button>
                    </Tooltip>
                </div>

                <div className="flex shrink-0 items-center justify-start border-r border-border-ultra-light px-2 sm:px-2 w-24">
                    <ArticlesCountInfo />
                </div>
                <div className="flex shrink-0 items-center justify-start border-r border-border-ultra-light px-2 sm:px-2 w-24">
                    <ArticleSelectionInfo />
                </div>

                <ArticleMetricsInfo />

                {isStandaloneMode && (
                    <div className="flex shrink-0 items-center justify-start border-r border-border-ultra-light px-2 sm:px-2 w-56 lg:w-72">
                        <ProjectSaveStatusInfo />
                    </div>
                )}
            </div>

            <div className="flex shrink-0 items-center min-w-0 max-w-[45vw] sm:max-w-[35vw]">
                <PlacementUpdateInfo />
            </div>
        </div>
    );
};
